<template>
	<div>
		<div class="account-type-grid">
			<AccountTypeLozenge v-for="accountType in accountTypes"
				:key="accountType.key"
				:accountType="accountType.label"
				:iconUrl="accountType.icon"
				:accountCount="getAccountCount(accountType.label)"
				:isAddButton="getAccountCount(accountType.label) === 0"
				:compactMode="cols !== 1"
				:text="accountType.text"
				:textIcon="accountType.textIcon"
				@click="handleAccountTypeClick(accountType)"
			/>
		</div>
		<MGButton
			v-if="showAddButton"
			class="add-account-button"
			:fullWidth="true"
			:noCaps="true"
			:text="`Add an account`"
			:plus="true"
			@click="handleAddAccountClick"
		/>
	</div>
</template>

<script>
import AccountTypeLozenge from './AccountTypeLozenge.vue';
import MGButton from './MGButton.vue';

export default {
	name: 'AccountTypeGrid',
	components: {
		AccountTypeLozenge, MGButton
	},
	emits: ['account-type-click', 'add-account-click', 'alt-action'],
	props: {
		currentAccounts: {
			type: Array,
			default: () => []
		},
		creditAccounts: {
			type: Array,
			default: () => []
		},
		savingAccounts: {
			type: Array,
			default: () => []
		},
		insuranceInfo: {
			type: Array,
			default: () => []
		},
		mortgageInfo: {
			type: Array,
			default: () => []
		},
		creditInfo: {
			type: Array,
			default: () => []
		},
		investmentInfo: {
			type: Array,
			default: () => []
		},
		additionalLongTerm: {
			type: Array,
			default: () => []
		},
		additionalInsurance: {
			type: Array,
			default: () => []
		},
		cols: {
			type: Number,
			default: 2
		},
		showAddButton: {
			type: Boolean,
			default: true
		},
		filter: {
			type: String,
			default: null
		}
	},
	data() {
		return {
			allAccountTypes: [
				{
					label: 'Current Account',
					text: 'Connect with <b>OPEN BANKING</b>',
					textIcon: require('@/assets/figureofeight.png'),
					altAction: 'openAddCurrentAccountDrawer',
					icon: require('@/assets/accountTypes/current-account.png'),
					walkthrough: 'money-account-types',
					key: 'current-account'
				},
				{
					label: 'Credit Card',
					text: 'Connect with <b>OPEN BANKING</b>',
					textIcon: require('@/assets/figureofeight.png'),
					altAction: 'openAddCurrentAccountDrawer',
					icon: require('@/assets/accountTypes/credit-card.png'),
					walkthrough: 'money-account-types',
					key: 'credit-card'
				},
				{
					label: 'Savings',
					text: 'Connect with <b>OPEN BANKING</b>',
					textIcon: require('@/assets/figureofeight.png'),
					altAction: 'openAddCurrentAccountDrawer',
					icon: require('@/assets/accountTypes/savings-account.png'),
					walkthrough: 'money-account-types',
					key: 'savings-account'
				},
				{
					label: 'Investments',
					icon: require('@/assets/accountTypes/investments.png'),
					walkthrough: 'money-account-types',
					key: 'investments'
				},
				{
					label: 'Pension',
					icon: require('@/assets/accountTypes/pension.png'),
					walkthrough: 'money-account-types',
					key: 'pension'
				},
				{
					label: 'Mortgage',
					icon: require('@/assets/accountTypes/mortgage.png'),
					walkthrough: 'money-account-types',
					key: 'mortgage'
				},
				{
					label: 'Loans',
					icon: require('@/assets/accountTypes/loans.png'),
					walkthrough: 'money-account-types',
					key: 'loans'
				},
				{
					label: 'Insurance Policy',
					icon: require('@/assets/accountTypes/insurance.png'),
					walkthrough: 'money-account-types',
					key: 'insurance-policy'
				}
			]
		}
	},
	computed: {
		accountTypes() {
			if (!this.filter) return this.allAccountTypes;

			const longTermKeys = ['investments', 'pension', 'mortgage', 'loans'];
			const dayToDayKeys = ['current-account', 'credit-card', 'savings-account'];

			if (this.filter === 'lt') {
				return this.allAccountTypes.filter(t => longTermKeys.includes(t.key));
			}
			if (this.filter === 'd2d') {
				return this.allAccountTypes.filter(t => dayToDayKeys.includes(t.key));
			}
			return this.allAccountTypes;
		}
	},
	methods: {
		getAccountCount(accountTypeLabel) {
			switch(accountTypeLabel) {
				case 'Current Account':
					return this.currentAccounts.length;
				case 'Credit Card':
					return this.creditAccounts.length;
				case 'Savings':
					return this.savingAccounts.length;
				case 'Insurance Policy':
					return this.insuranceInfo.length + (this.additionalInsurance?.length || 0);
				case 'Mortgage':
					return this.mortgageInfo.length + (this.additionalLongTerm?.length || 0);
				case 'Loans':
					return this.creditInfo.length;
				case 'Pension':
					return this.investmentInfo.filter(account => account.accountType?.name === 'Pension').length;
				case 'Investments':
					return this.investmentInfo.filter(account => account.accountType?.name === 'Investment').length;
				default:
					return 0;
			}
		},
		handleAddAccountClick() {
			this.$emit('add-account-click');
		},
		handleAccountTypeClick(accountType) {
			// If altAction exists and we're in compact mode, emit alt-action event
			if (accountType.altAction && this.cols === 1) {
				this.$emit('alt-action', accountType);
			} else {
				this.$emit('account-type-click', accountType);
			}
		}
	}
}
</script>

<style scoped>
.account-type-grid {
	display: grid;
	grid-template-columns: v-bind("'repeat(' + cols + ', 1fr)'");
	gap: 8px;
}
</style>
